import NotLoggedInContainer from '../base/NotLoggedInContainer'
import { styled, Typography, useTheme } from '@mui/material'
import TextInput from '../base/TextInput'
import { useEffect, useState } from 'react'
import OpaqueButton from '../base/buttons/OpaqueButton'
import LinkButton from '../base/buttons/LinkButton'
import { LoginError } from '../../exceptions'
import { login, postSSOLogin } from '../../requests/notLoggedInRequests'
import redirectToHomepage from '../../logic/redirectToHomepage'
import { useRouter } from 'next/router'
import ErrorMessage from '../base/ErrorMessage'
import PasswordTextInput from '../base/PasswordTextInput'
import { navigationPush } from '../../navigation/studentPages'
import notLoggedInPages from '../../navigation/notLoggedInPages'
import GoogleButton from '../base/buttons/GoogleButton'
import AppleButton from '../base/buttons/AppleButton'
import SignUpDialog from './SignUpDialog'
import ValidationCodeDialog from './ValidationCodeDialog'
import CleverButton from '../base/buttons/CleverButton'
import ClassLinkButton from "../base/buttons/ClassLinkButton"
import { SSO_TYPES } from "../../utils/utils"

const Container = styled('div')({
  padding: '70px 16px 120px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const LogInWindow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.3)',
  width: '100%',
  maxWidth: '880px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '350px',
  },
}))

const LogInForm = styled('form')({
  maxWidth: '400px',
  width: '100%',
  padding: '32px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  rowGap: '20px',
})

const VisualSplash = styled('div')(({ theme }) => ({
  backgroundImage: `url(/static/sign-in-background.png), linear-gradient(${theme.palette.loginHeroBackground.gradientStart}, ${theme.palette.loginHeroBackground.gradientEnd})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '480px',
  minHeight: '400px',
  rowGap: '24px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}))

const SplashText = styled(Typography)(({ theme }) => ({
  color: theme.palette.mainText.fullWhite,
}))

const LoginTextField = styled(TextInput)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.mainText.main,
    },
  },
}))

const LoginPasswordTextField = styled(PasswordTextInput)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.mainText.main,
    },
  },
}))

const LoginButton = styled(OpaqueButton)(({ theme }) => ({
  width: '100%',
  height: '38px',
  borderRadius: '24px',
  backgroundColor: theme.palette.background.darker,
  '&& .MuiTouchRipple-child': {
    backgroundColor: theme.palette.background.darker,
    filter: 'brightness(85%)',
  },
  '&:hover': {
    backgroundColor: theme.palette.background.darker,
    filter: 'brightness(65%)',
  },
}))

const AdditionalActions = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '8px',
  marginTop: '40px',
})

const BlueStroke = styled('div')(({ theme }) => ({
  background: 'linear-gradient(270deg, #60C4C8, #76BFFE)',
  height: '3px',
  width: '50%',
}))

const Divider = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  columnGap: '12px',
  width: '100%',
  alignItems: 'center',
})

const HorizontalLine = styled('div')(({ theme }) => ({
  height: '1px',
  backgroundColor: 'rgba(0, 0, 0, 0.25)',
  flexGrow: 1,
}))

const StyledGoogleButton = styled(GoogleButton)({
  width: '100%',
})

const StyledAppleButton = styled(AppleButton)({
  width: '100%',
})

const StyledCleverButton = styled(CleverButton)({
  width: '100%',
})

const StyledClassLinkButton = styled(ClassLinkButton)({
  width: '100%',
})

const mapLoginErrorCode = (loginError) => {
  if (loginError.code === 'not_found') {
    return {
      type: 'email',
      message: 'This email doesn\'t seem to be associated with an account. Do you need to create one?',
    }
  }
  if (loginError.code === 'not_verified') {
    return {
      type: 'email',
      message: 'You need to confirm your email address to log in to this account.\n\nIf you cannot find the confirmation email we sent, you can also confirm your account by pressing Forgot Password? below, and following the steps to reset your password.',
    }
  }
  if (loginError.code === 'account_disabled') {
    return {
      type: 'generic',
      message: loginError.message,
    }
  }
  if (loginError.code === 'wrong_password') {
    return {
      type: 'password',
      message: `This password you entered is incorrect. Try again or reset your password. ${loginError.numAttemptsRemaining <= 5 ? `For security reasons, you have ${loginError.numAttemptsRemaining} attempts remaining.` : ''}`,
    }
  }
  if (loginError.code === 'too_many_login_attempts') {
    return {
      type: 'generic',
      message: loginError.message,
    }
  }
  if (loginError.code === 'google_sso') {
    return {
      type: 'email',
      message: 'This email was used to sign up via Google. Please log in with Google below.',
    }
  }
  if (loginError.code === 'apple_sso') {
    return {
      type: 'email',
      message: 'This email was used to sign up via Apple. Please log in with Apple below.',
    }
  }
  if (loginError.code === 'clever_sso') {
    return {
      type: 'email',
      message: 'This email was used to sign up via Clever. Please log in with Clever below.',
    }
  }
  return {
    type: 'generic',
    message: 'There was an error logging you in. Please try again.',
  }
}

const LogIn = ({ student = false }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [requestingLogin, setRequestingLogin] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [genericError, setGenericError] = useState('')
  const [showSignUpDialog, setShowSignUpDialog] = useState(false)
  const [showValidationCodeDialog, setShowValidationCodeDialog] = useState(false)
  const [loadingClever, setLoadingClever] = useState(false)
  const [loadingClassLink, setLoadingClassLink] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const [loadingApple, setLoadingApple] = useState(false)
  const router = useRouter()
  const { code, ssoType } = router.query

  const theme = useTheme()
  const onCleverLoginSuccess = async (accessToken) => {
    try {
      setLoadingClever(true)
      setGenericError('')
      const jwt = await postSSOLogin({
        token: accessToken,
        ssoType: 'Clever',
      })
      localStorage.setItem('access_token', jwt)
      await redirectToHomepage(router)
    } catch (e) {
      if (e.code === 'not_found') {
        setGenericError(`We couldn't find an account for this user. Please sign up to get started.`)
      } else if (e.code === 'found_not_Clever_but_ClassLink') {
        setGenericError('This account was created via ClassLink. Please log in with ClassLink.')
      } else if (e.code === 'found_not_Clever_but_Apple') {
        setGenericError('This account was created via Apple. Please log in with Apple.')
      } else if (e.code === 'found_not_Clever_but_Google') {
        setGenericError('This account was created via Google. Please log in with Google.')
      } else if (e.code === 'found_not_Clever_but_null') {
        setGenericError('This account was not created with Clever. Please log in with email/password above.')
      } else if (e.code === 'code_already_used') {
        setGenericError('There was an error logging you in. To Log in with Clever, please click the button above.')
      } else {
        setGenericError('There was an error logging you in. Please try again.')
      }
      setLoadingClever(false)
    }
  }
  const onClassLinkLoginSuccess = async (accessToken) => {
    try {
      setLoadingClassLink(true)
      setGenericError('')
      const jwt = await postSSOLogin({
        token: accessToken,
        ssoType: 'ClassLink',
      })
      localStorage.setItem('access_token', jwt)
      await redirectToHomepage(router)
    } catch (e) {
      if (e.code === 'not_found') {
        setGenericError(`We couldn't find an account for this user. Please sign up to get started.`)
      } else if (e.code === 'found_not_ClassLink_but_Clever') {
        setGenericError('This account was created via Clever. Please log in with Clever.')
      } else if (e.code === 'found_not_ClassLink_but_Apple') {
        setGenericError('This account was created via Apple. Please log in with Apple.')
      } else if (e.code === 'found_not_ClassLink_but_Google') {
        setGenericError('This account was created via Google. Please log in with Google.')
      } else if (e.code === 'found_not_ClassLink_but_null') {
        setGenericError('This account was not created with ClassLink. Please log in with email/password above.')
      } else if (e.code === 'code_already_used') {
        setGenericError('There was an error logging you in. To Log in with ClassLink, please click the button above.')
      } else {
        setGenericError('There was an error logging you in. Please try again.')
      }
      setLoadingClassLink(false)
    }
  }
  const onGoogleLoginSuccess = async (accessToken) => {
    try {
      setLoadingGoogle(true)
      const jwt = await postSSOLogin({
        token: accessToken,
        ssoType: 'Google',
      })
      localStorage.setItem('access_token', jwt)
      await redirectToHomepage(router)
    } catch (e) {
      if (e.code === 'not_found') {
        setGenericError(`We couldn't find an account for this user. Please try another email or sign up to get started.`)
      } else if (e.code === 'found_not_Google_but_ClassLink') {
        setGenericError('This account was created via ClassLink. Please log in with ClassLink.')
      } else if (e.code === 'found_not_Google_but_Apple') {
        setGenericError('This account was created via Apple. Please log in with Apple.')
      } else if (e.code === 'found_not_Google_but_null') {
        setGenericError('This account was not created with Google. Please log in with email/password above.')
      } else {
        setGenericError('There was an error logging you in. Please try again.')
      }
      setLoadingGoogle(false)
    }
  }
  const onAppleLoginSuccess = async (idToken) => {
    try {
      setLoadingApple(true)
      const jwt = await postSSOLogin({
        token: idToken,
        ssoType: 'Apple',
      })
      localStorage.setItem('access_token', jwt)
      await redirectToHomepage(router)
    } catch (e) {
      if (e.code === 'not_found') {
        setGenericError('We couldn\'t find an account for this user. Please try another email or sign up to get started.')
      } else if (e.code === 'found_not_Apple_but_ClassLink') {
        setGenericError('This account was created via ClassLink. Please log in with ClassLink.')
      } else if (e.code === 'found_not_Apple_but_Google') {
        setGenericError('This account was created via Google. Please log in with Google.')
      } else if (e.code === 'found_not_Apple_but_null') {
        setGenericError('This account was not created with Apple. Please log in with email/password above.')
      } else {
        setGenericError('There was an error logging you in. Please try again.')
      }
      setLoadingApple(false)
    }
  }
  const onSSOLoginError = () => {
    setGenericError('There was an error logging you in, please try again.')
  }
  const submitLogin = async (e) => {
    e.preventDefault()
    if (!email) {
      setEmailError('Email cannot be empty')
    }
    if (!password) {
      setPasswordError('Password cannot be empty')
    }
    if (!email || !password) {
      return
    }

    try {
      setRequestingLogin(true)
      const jwt = await login(email, password)
      try {
        localStorage.setItem('access_token', jwt)
        await redirectToHomepage(router)
      } catch (e) {
        throw new LoginError('error', 'There was an error logging you in. Please try again.')
      }
    } catch (e) {
      if (e.code === 'not_verified_requires_code') {
        setShowValidationCodeDialog(true)
        return
      }
      const errorResponse = mapLoginErrorCode(e)
      setEmailError('')
      setPasswordError('')
      setGenericError('')
      if (errorResponse.type === 'email') {
        setEmailError(errorResponse.message)
      } else if (errorResponse.type === 'password') {
        setPasswordError(errorResponse.message)
      } else {
        setGenericError(errorResponse.message)
      }
    } finally {
      setRequestingLogin(false)
    }
  }

  // handle Instant Log-In links that go directly to /signIn with code as query param
  useEffect(() => {
    if (!code || !ssoType || loadingClever || loadingClassLink) return

    (async () => {
      if (ssoType === SSO_TYPES.CLEVER) {
        await onCleverLoginSuccess(code)
      } else if (ssoType === SSO_TYPES.CLASSLINK) {
        await onClassLinkLoginSuccess(code)
      } else {
        console.error('Invalid SSO type for login:', ssoType)
      }
    })()
  }, [code, ssoType])

  return (
    <NotLoggedInContainer student={student} type={'login'} dontRedirectAuthenticated>
      <Container>
        <LogInWindow>
          <LogInForm>
            <Typography variant={'h5'} sx={{ marginBottom: '-8px', fontWeight: 'bold' }}>
              Log In
            </Typography>
            <BlueStroke />
            <LoginTextField
              variant={'outlined'}
              label={'Email'}
              value={email}
              errorMessage={emailError}
              onChange={(e) => {
                setEmailError('')
                setGenericError('')
                setEmail(e.target.value)
              }}
              width={'100%'}
            />
            <LoginPasswordTextField
              variant={'outlined'}
              label={'Password'}
              value={password}
              errorMessage={passwordError}
              onChange={(e) => {
                setPasswordError('')
                setGenericError('')
                setPassword(e.target.value)
              }}
              width={'100%'}
            />
            <LoginButton
              title={'Log In'}
              disabled={loadingGoogle || loadingApple || loadingClassLink || loadingClever}
              id={'login-button'}
              loading={requestingLogin}
              onClick={submitLogin}
              type={'submit'}
              data-testid={'logInButton'}
              textStyle={{
                textTransform: 'none',
              }}
            />
            <LinkButton
              textStyle={{
                color: theme.palette.activeBlueColor,
              }}
              title={'Forgot your password?'}
              onClick={() => {
                navigationPush(notLoggedInPages.forgotPassword.getRoute(), router, notLoggedInPages.forgotPassword.v1)
              }}
            />
            <Divider>
              <HorizontalLine />
              <Typography variant={'body2'} sx={{ fontSize: 16 }}>
                or
              </Typography>
              <HorizontalLine />
            </Divider>
            <StyledCleverButton
              loading={loadingClever}
              disabled={loadingClassLink || loadingApple || loadingGoogle || requestingLogin}
              buttonText={'Log in with Clever'}
              onSuccess={onCleverLoginSuccess}
              onError={onSSOLoginError}
            />
            <StyledClassLinkButton
              loading={loadingClassLink}
              disabled={loadingClever || loadingApple || loadingGoogle || requestingLogin}
              buttonText={'Log in with ClassLink'}
              onSuccess={onClassLinkLoginSuccess}
              onError={onSSOLoginError}
            />
            <StyledGoogleButton
              loading={loadingGoogle}
              disabled={loadingApple || loadingClassLink || loadingClever || requestingLogin}
              buttonText={'Log in with Google'}
              onSuccess={onGoogleLoginSuccess}
              onError={onSSOLoginError}
            />
            <StyledAppleButton
              loading={loadingApple}
              disabled={loadingGoogle || loadingClassLink || loadingClever || requestingLogin}
              buttonText={'Log in with Apple'}
              onSuccess={onAppleLoginSuccess}
              onError={onSSOLoginError}
            />
            <ErrorMessage errorMessage={genericError} />
            <AdditionalActions>
              <LinkButton
                title={<>
                  Don&apos;t have an account?&nbsp;
                  <span style={{
                    color: theme.palette.activeBlueColor,
                  }}>
                    Sign up
                  </span></>
                }
                onClick={() => {
                  if (!student) {
                    setShowSignUpDialog(true)
                  } else {
                    navigationPush(notLoggedInPages.studentSignUp.getRoute(), router, notLoggedInPages.studentSignUp.v1)
                  }
                }}
              />
            </AdditionalActions>
          </LogInForm>
          <VisualSplash>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              Reclaim Your Focus
            </SplashText>
            <SplashText variant={'h3'} sx={{ textAlign: 'center' }}>
              Unlock Your Potential
            </SplashText>
          </VisualSplash>
        </LogInWindow>
      </Container>
      <SignUpDialog
        showDialog={showSignUpDialog}
        setShowDialog={setShowSignUpDialog}
      />
      <ValidationCodeDialog
        showDialog={showValidationCodeDialog}
        setShowDialog={setShowValidationCodeDialog}
        validatingEmail={email}
      />
    </NotLoggedInContainer>
  )
}

export default LogIn
